/**
 * Public shared map viewer — /m/:layerId
 * Robust load, auto-zoom, map measurement labels, attribute form + QR.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Collapse,
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import BadgeIcon from '@mui/icons-material/Badge';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MapComponent from './Map';
import PropertyCardWizard from './propertyCard/PropertyCardWizard';
import PropertyAttributeForm from './propertyCard/PropertyAttributeForm';
import DigipinDisclaimer from './site/DigipinDisclaimer';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { replaceAllLayers, type Layer } from '../store/slices/layersSlice';
import { setCurrentBasemap, setMapView } from '../store/slices/mapSlice';
import { normalizeFeaturesForMap } from '../utils/geojsonToVectorLayer';
import { DEMO_PARCEL_LAYER } from '../utils/demoSampleParcel';
import { API_BASE } from '../config/api';
import { mapNavigator } from '../services/MapNavigator';
import { trackEvent } from '../services/analyticsService';
import { attachParcelLabels, type ParcelLabelOverlayHandle } from '../utils/parcelMapLabels';

/** Padding [top, right, bottom, left] so fit clears share chrome */
const PUBLIC_FIT_PADDING: [number, number, number, number] = [110, 48, 160, 48];

function buildPublicLayer(raw: any): Layer {
  const id = String(raw.id || 'shared-layer');
  const props0 = raw.features?.[0]?.properties || {};
  const isParcel =
    !!props0.DIGIPIN ||
    !!props0.Area_Label ||
    !!props0.Side_1_m ||
    !!props0.Side_1_Label ||
    raw.geometryType === 'Polygon';

  const normalizedFeatures = normalizeFeaturesForMap(
    (raw.features || []).map((f: any, i: number) => ({
      id: f.id || `shared-f-${i}`,
      layerId: id,
      geometry: f.geometry,
      properties: f.properties || {},
    }))
  );

  // Enrich features with Share_URL for QR if missing
  const shareFallback =
    typeof window !== 'undefined' ? `${window.location.origin}/m/${id}` : `/m/${id}`;
  const features = normalizedFeatures.map((f) => ({
    ...f,
    properties: {
      ...f.properties,
      Share_URL: f.properties?.Share_URL || f.properties?.QR_URL || shareFallback,
      QR_URL: f.properties?.QR_URL || f.properties?.Share_URL || shareFallback,
    },
  }));

  return {
    id,
    name: raw.name || 'Shared Layer',
    type: 'vector',
    geometryType: raw.geometryType,
    visible: true,
    opacity: 1,
    zIndex: 5,
    symbology: raw.symbology || {
      type: 'simple',
      fillColor: isParcel ? 'rgba(132, 204, 22, 0.35)' : 'rgba(59, 130, 246, 0.25)',
      strokeColor: isParcel ? '#a3e635' : '#60A5FA',
      strokeWidth: isParcel ? 3.5 : 2.5,
      opacity: 0.9,
    },
    crs: raw.crs || 'EPSG:4326',
    features,
    featureCount: features.length,
  };
}

export default function PublicMapView() {
  const { layerId } = useParams<{ layerId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [layerName, setLayerName] = useState<string>('Shared Map');
  const [featureCount, setFeatureCount] = useState<number>(0);
  const [loadedLayers, setLoadedLayers] = useState<Layer[]>([]);
  const [copied, setCopied] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [propertyCardOpen, setPropertyCardOpen] = useState(false);
  const [zoomHint, setZoomHint] = useState<string | null>(null);
  const zoomedForId = useRef<string | null>(null);
  const labelHandleRef = useRef<ParcelLabelOverlayHandle | null>(null);

  const firstFeature = useMemo(() => loadedLayers[0]?.features?.[0], [loadedLayers]);
  const firstFeatureProps = useMemo(
    () => (firstFeature?.properties || {}) as Record<string, unknown>,
    [firstFeature]
  );

  const placeParcelLabels = useCallback((layers: Layer[]) => {
    const map = mapNavigator.getMap();
    if (!map) return;
    labelHandleRef.current?.clear();
    const f = layers[0]?.features?.[0];
    if (!f?.geometry) return;
    const props = (f.properties || {}) as Record<string, unknown>;
    labelHandleRef.current = attachParcelLabels(map, {
      geometry: f.geometry as { type: string; coordinates: unknown },
      name: String(props.name || layers[0]?.name || ''),
      digipin: props.DIGIPIN ? String(props.DIGIPIN) : null,
      areaLabel: props.Area_Label ? String(props.Area_Label) : null,
      showSides: true,
      showCenter: true,
    });
  }, []);

  const fitToShared = useCallback(
    async (layers: Layer[], reason: string) => {
      if (!layers.length) return;
      const ok = await mapNavigator.zoomToAllLayersWhenReady(layers, {
        padding: PUBLIC_FIT_PADDING,
        duration: 450,
        maxZoom: 19,
        attempts: 50,
      });
      if (!ok) {
        setZoomHint('Map still loading — tap Fit to features');
      } else {
        setZoomHint(null);
        // Labels after fit so they sit correctly
        window.setTimeout(() => placeParcelLabels(layers), 200);
      }
      const map = mapNavigator.getMap();
      const view = map?.getView();
      if (view) {
        const center = view.getCenter();
        const zoom = view.getZoom();
        if (center && zoom != null) {
          try {
            const [lon, lat] = (await import('ol/proj')).toLonLat(center);
            dispatch(setMapView({ center: [lon, lat], zoom, rotation: view.getRotation() || 0 }));
          } catch {
            /* ignore */
          }
        }
      }
      console.debug('[PublicMapView] fit', reason, ok);
    },
    [dispatch, placeParcelLabels]
  );

  useEffect(() => {
    if (!layerId) {
      setError('Invalid map link');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchPublicLayer = async () => {
      try {
        setLoading(true);
        setError(null);
        zoomedForId.current = null;

        let publicLayer: Layer;

        if (layerId === 'demo-sample-parcel-101' || layerId.startsWith('demo-')) {
          publicLayer = DEMO_PARCEL_LAYER;
        } else {
          const res = await fetch(`${API_BASE}/layers/public/${layerId}`, {
            credentials: 'include',
            headers: { Accept: 'application/json' },
          });

          if (!res.ok) {
            if (
              res.status === 404 &&
              (layerId.includes('demo') || layerId.includes('sample') || layerId.includes('parcel'))
            ) {
              publicLayer = DEMO_PARCEL_LAYER;
            } else if (res.status === 404) {
              throw new Error('This map or layer was not found or is no longer available.');
            } else {
              throw new Error('Failed to load shared map. Please try again.');
            }
          } else {
            const data = await res.json();
            const rawLayer = data.data;
            if (!rawLayer) throw new Error('No layer data returned.');
            publicLayer = buildPublicLayer(rawLayer);
          }
        }

        if (cancelled) return;

        if (!publicLayer.features?.length) {
          throw new Error('This shared map has no drawable features.');
        }

        setLayerName(publicLayer.name);
        setFeatureCount(publicLayer.features.length);
        setLoadedLayers([publicLayer]);

        // Satellite looks better for property parcels; streets as fallback style
        const looksLikeParcel =
          publicLayer.geometryType === 'Polygon' ||
          !!(publicLayer.features?.[0]?.properties as any)?.DIGIPIN;
        dispatch(setCurrentBasemap(looksLikeParcel ? 'satellite' : 'osm'));
        dispatch(replaceAllLayers({ layers: [publicLayer], activeLayerId: publicLayer.id }));

        // Optimistic center before map mounts (reduces world-flash)
        try {
          const extent = mapNavigator.calculateAllLayersExtent([publicLayer]);
          if (extent) {
            const cx = (extent[0] + extent[2]) / 2;
            const cy = (extent[1] + extent[3]) / 2;
            // approximate zoom: leave fitWhenReady for precise
            const { toLonLat } = await import('ol/proj');
            const [lon, lat] = toLonLat([cx, cy]);
            dispatch(setMapView({ center: [lon, lat], zoom: 16, rotation: 0 }));
          }
        } catch {
          /* mapNavigator extent calc works without map */
        }

        trackEvent('share_view', {
          layerId,
          featureCount: publicLayer.features.length,
          name: publicLayer.name,
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Error loading map');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPublicLayer();
    return () => {
      cancelled = true;
    };
  }, [layerId, dispatch]);

  // Auto-zoom once after load when map is ready
  useEffect(() => {
    if (loading || error || !loadedLayers.length) return;
    const id = loadedLayers[0]?.id || layerId || '';
    if (zoomedForId.current === id) return;
    zoomedForId.current = id;

    let cancelled = false;
    (async () => {
      // Give Map a tick to mount + register with mapNavigator
      await new Promise((r) => requestAnimationFrame(() => r(undefined)));
      await new Promise((r) => setTimeout(r, 80));
      if (cancelled) return;
      await fitToShared(loadedLayers, 'auto');
      // One more fit after tiles/layout
      await new Promise((r) => setTimeout(r, 400));
      if (!cancelled) await fitToShared(loadedLayers, 'auto-settle');
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, error, loadedLayers, layerId, fitToShared]);

  // Re-fit on resize + re-place labels
  useEffect(() => {
    if (!loadedLayers.length) return;
    const onResize = () => {
      window.setTimeout(() => {
        void fitToShared(loadedLayers, 'resize');
      }, 100);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      labelHandleRef.current?.clear();
      labelHandleRef.current = null;
    };
  }, [loadedLayers, fitToShared]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      setCopied(true);
    }
  };

  const handleFit = () => {
    void fitToShared(loadedLayers, 'manual');
  };

  return (
    <Box sx={{ position: 'fixed', inset: 0, bgcolor: '#0f1712', overflow: 'hidden' }}>
      {!loading && !error && (
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <MapComponent useReduxState={true} />
        </Box>
      )}

      {loading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 2,
            color: '#c7ec9b',
          }}
        >
          <CircularProgress color="inherit" size={40} />
          <Typography variant="body1" sx={{ color: '#b8cabd', fontWeight: 500 }}>
            Loading shared GeoVera map…
          </Typography>
        </Box>
      )}

      {error && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 3,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 4,
              maxWidth: 440,
              bgcolor: 'rgba(20, 31, 24, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            <MapIcon sx={{ fontSize: 48, color: '#e57373', mb: 1 }} />
            <Typography variant="h6" sx={{ color: '#f1f5e9', fontWeight: 700, mb: 1 }}>
              Map Not Found
            </Typography>
            <Typography variant="body2" sx={{ color: '#b8cabd', mb: 3 }}>
              {error}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
                sx={{ textTransform: 'none', borderColor: '#84cc16', color: '#c7ec9b' }}
              >
                Retry
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{
                  bgcolor: '#84cc16',
                  color: '#0d190e',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#a3e635' },
                }}
              >
                GeoVera Home
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}

      {!loading && !error && (
        <>
          {/* Top bar */}
          <Paper
            elevation={8}
            sx={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 100,
              width: '94%',
              maxWidth: 860,
              bgcolor: 'rgba(15, 25, 18, 0.94)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(199, 236, 155, 0.28)',
              borderRadius: 3,
              p: { xs: 1.25, sm: 1.75 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: 'rgba(132, 204, 22, 0.15)',
                  border: '1px solid rgba(132, 204, 22, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#a3e635',
                  flexShrink: 0,
                }}
              >
                <MapIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography
                    noWrap
                    sx={{
                      color: '#f1f5e9',
                      fontWeight: 800,
                      fontSize: { xs: '0.9rem', sm: '1.05rem' },
                      maxWidth: { xs: 160, sm: 360 },
                    }}
                  >
                    {layerName}
                  </Typography>
                  <Chip
                    label={`${featureCount} ${featureCount === 1 ? 'feature' : 'features'}`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(199, 236, 155, 0.15)',
                      color: '#c7ec9b',
                      fontSize: 11,
                      fontWeight: 700,
                      height: 20,
                    }}
                  />
                  <Chip
                    label="View only"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(59, 130, 246, 0.15)',
                      color: '#93C5FD',
                      fontSize: 10,
                      fontWeight: 700,
                      height: 20,
                    }}
                  />
                </Stack>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: '#889f8f', display: { xs: 'none', sm: 'block' } }}
                >
                  Shared map · GeoVera WebGIS · Auto-zoomed to features
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
              <Tooltip title="Fit to features">
                <IconButton
                  onClick={handleFit}
                  size="small"
                  sx={{
                    color: '#b8cabd',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    '&:hover': { bgcolor: 'rgba(132, 204, 22, 0.12)', color: '#a3e635' },
                  }}
                >
                  <CenterFocusStrongIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Property card">
                <Button
                  size="small"
                  startIcon={<BadgeIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setPropertyCardOpen(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 12,
                    color: '#38bdf8',
                    border: '1px solid rgba(56, 189, 248, 0.4)',
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 1.5,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: 'rgba(56, 189, 248, 0.12)' },
                  }}
                >
                  Property card
                </Button>
              </Tooltip>
              <Tooltip title="Copy share link">
                <IconButton
                  onClick={handleCopyLink}
                  size="small"
                  sx={{
                    color: '#b8cabd',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                size="medium"
                startIcon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
                endIcon={<ArrowOutwardIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate('/map')}
                sx={{
                  bgcolor: '#84cc16',
                  color: '#0d190e',
                  fontWeight: 800,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  textTransform: 'none',
                  px: { xs: 1.25, sm: 2 },
                  borderRadius: 2,
                  boxShadow: '0 0 20px rgba(132, 204, 22, 0.35)',
                  display: { xs: 'none', sm: 'inline-flex' },
                  '&:hover': { bgcolor: '#a3e635' },
                }}
              >
                Create your map
              </Button>
            </Stack>
          </Paper>

          {/* Attribute form + QR (side lengths, area, DIGIPIN, etc.) */}
          {firstFeature && (
            <Paper
              elevation={8}
              sx={{
                position: 'absolute',
                left: 12,
                top: { xs: 88, sm: 96 },
                zIndex: 95,
                width: { xs: 'calc(100% - 24px)', sm: 300 },
                maxWidth: 340,
                maxHeight: { xs: '42vh', sm: 'calc(100vh - 220px)' },
                overflow: 'auto',
                bgcolor: 'rgba(11, 19, 43, 0.94)',
                border: '1px solid rgba(132, 204, 22, 0.35)',
                borderRadius: 2.5,
                backdropFilter: 'blur(12px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
              }}
            >
              <Box
                onClick={() => setDetailsOpen((v) => !v)}
                sx={{
                  px: 1.5,
                  py: 1.1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  borderBottom: detailsOpen ? '1px solid rgba(255,255,255,0.08)' : 'none',
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'rgba(11, 19, 43, 0.98)',
                  zIndex: 1,
                }}
              >
                <Typography
                  sx={{ color: '#c7ec9b', fontWeight: 800, fontSize: 12, letterSpacing: '0.06em' }}
                >
                  PROPERTY ATTRIBUTES
                </Typography>
                {detailsOpen ? (
                  <ExpandLessIcon sx={{ color: '#94A3B8', fontSize: 18 }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: '#94A3B8', fontSize: 18 }} />
                )}
              </Box>
              <Collapse in={detailsOpen}>
                <Box sx={{ px: 1.5, py: 1.25 }}>
                  <PropertyAttributeForm
                    name={layerName}
                    properties={firstFeatureProps}
                    geometry={
                      firstFeature.geometry as { type: string; coordinates: unknown } | undefined
                    }
                    shareUrl={
                      typeof window !== 'undefined'
                        ? window.location.href
                        : firstFeatureProps.Share_URL
                          ? String(firstFeatureProps.Share_URL)
                          : null
                    }
                    onCopyShare={handleCopyLink}
                    compact
                  />
                </Box>
              </Collapse>
            </Paper>
          )}

          {zoomHint && (
            <Chip
              label={zoomHint}
              onClick={handleFit}
              sx={{
                position: 'absolute',
                top: 100,
                right: 16,
                zIndex: 96,
                bgcolor: 'rgba(217, 119, 6, 0.9)',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            />
          )}

          {/* DIGIPIN note */}
          <Box
            sx={{
              position: 'absolute',
              bottom: isAuthenticated ? 16 : 100,
              left: 12,
              right: 12,
              zIndex: 90,
              maxWidth: 520,
              mx: 'auto',
            }}
          >
            <DigipinDisclaimer
              variant="inline"
              sx={{
                color: 'rgba(184, 202, 189, 0.85)',
                fontSize: 10,
                textAlign: 'center',
                textShadow: '0 1px 4px rgba(0,0,0,0.8)',
              }}
            />
          </Box>

          {/* Bottom CTA */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 100,
              width: '94%',
              maxWidth: 640,
              bgcolor: 'rgba(9, 21, 18, 0.95)',
              border: '1px solid rgba(132, 204, 22, 0.35)',
              borderRadius: 3,
              p: 1.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              boxShadow: '0 12px 36px rgba(0,0,0,0.55)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 800 }}>
                {isAuthenticated
                  ? 'Open your free WebGIS workspace'
                  : 'Full WebGIS is free forever'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block' }}>
                {isAuthenticated
                  ? 'Draw, measure, DIGIPIN, share — same tools as this map.'
                  : 'Open free — sign in only when you want cloud save & share links.'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              {!isAuthenticated && (
                <Button
                  variant="outlined"
                  onClick={() =>
                    navigate(`/login?redirect=${encodeURIComponent(`/m/${layerId || ''}`)}`)
                  }
                  sx={{
                    borderColor: 'rgba(132, 204, 22, 0.5)',
                    color: '#c7ec9b',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    borderRadius: 2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Sign in free
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => navigate('/map')}
                sx={{
                  bgcolor: '#84cc16',
                  color: '#000',
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 2,
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: '#a3e635' },
                }}
              >
                Open free map
              </Button>
            </Stack>
          </Box>
        </>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={2800}
        onClose={() => setCopied(false)}
        message="Share link copied"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <PropertyCardWizard open={propertyCardOpen} onClose={() => setPropertyCardOpen(false)} />
    </Box>
  );
}
